import React, { FC, useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Box } from '@mui/material';
import MobileNavBar from './MobileNavBar';
import MobileBottomNav from './MobileBottomNav';
import AuroraBackground from '../AuroraBackground';
import { MobileHeaderProvider, useMobileImmersiveEnabled, useMobilePanelScrollEl } from './MobileHeaderContext';
import { AuthSheetProvider } from './AuthSheetContext';
import { useScrollTopOnPush } from '../../utils/useScrollRestoration';
import Routes from '../../Routes';

/**
 * Inner chrome, rendered inside the header provider so it can react to "panel
 * mode": when a page uses SwipeTabViews (per-tab scrolling), the layout stops
 * scrolling itself, the glass header overlays the panels, and hide-on-scroll
 * watches the active panel's scroller instead.
 */
const MobileAppChrome: FC<{ isFullscreen: boolean }> = ({ isFullscreen }) => {
  const theme = useTheme();
  // Held in state (not a ref) so the nav bar re-renders once the scroll
  // container mounts and can watch it for the hide-on-scroll behavior.
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);
  const panelScrollEl = useMobilePanelScrollEl();
  const panelMode = panelScrollEl !== null;
  // A page can take over the screen from inside (an open chat), which routes
  // alone cannot express.
  const immersive = useMobileImmersiveEnabled();
  const hideChrome = isFullscreen || immersive;
  const scrollTarget = panelScrollEl ?? scrollEl;
  // Forward navigations start at the top of the persistent layout scroller.
  // Panel scrollers (SwipeTabViews) are fresh DOM on mount and manage their own
  // positions, so only the layout element is reset here.
  useScrollTopOnPush(scrollEl);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        height: 'var(--layout-viewport-height)',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <AuroraBackground />
      {/* The nav bar lives inside the scroll area so content scrolls behind it
          and shows through the frosted glass. In panel mode it overlays the
          full-height tab panels instead of occupying a layout slot. */}
      <Box
        ref={setScrollEl}
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          overflowY: panelMode ? 'hidden' : 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          zIndex: 1,
          // In panel mode the panels pad for the bottom nav themselves so their
          // content can scroll behind its glass.
          // Immersive pages own the full height: no bottom bar means no reserve.
          pb: panelMode || immersive ? 0 : theme.tokens.layout.bottomNavSafePadding,
          // Pages are flex items in this fixed-height column: without this the
          // browser shrinks a tall page's box to its min-height, its content
          // spills as invisible overflow past the bottom padding, and the last
          // controls (e.g. the submit page's Post button) end up flush with the
          // screen edge underneath the fixed bottom nav.
          '& > *': { flexShrink: 0 },
        }}
      >
        {!hideChrome && <MobileNavBar scrollTarget={scrollTarget} overlay={panelMode} />}
        <Routes isMobileApp />
      </Box>

      {!immersive && <MobileBottomNav scrollTarget={scrollTarget} />}
    </Box>
  );
};

const MobileAppLayout: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = useRef(location);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return undefined;
    }

    let listener: { remove: () => void } | undefined;

    const registerBackButtonHandler = async () => {
      listener = await CapacitorApp.addListener('backButton', ({ canGoBack }) => {
        const currentPath = locationRef.current.pathname;
        const routerHistoryIndex = window.history.state?.idx;
        const hasRouterHistory = typeof routerHistoryIndex === 'number' && routerHistoryIndex > 0;

        if (hasRouterHistory || canGoBack) {
          navigate(-1);
          return;
        }

        if (currentPath !== '/') {
          navigate('/', { replace: true });
        }
      });
    };

    registerBackButtonHandler();

    return () => {
      listener?.remove();
    };
  }, [navigate]);

  const fullscreenRoutes = [/^\/comments\//, /^\/submit/];
  const isFullscreen = fullscreenRoutes.some((pattern) => pattern.test(location.pathname));

  return (
    <MobileHeaderProvider>
      <AuthSheetProvider>
        <MobileAppChrome isFullscreen={isFullscreen} />
      </AuthSheetProvider>
    </MobileHeaderProvider>
  );
};

export default MobileAppLayout;
